/**
 * QuickTapsFeaturedTableV2 — redesigned Featured Quick Taps reorder
 * list. Mirrors the patterns from CategoriesTableV2 (drag-to-reorder
 * via @dnd-kit, LibTable / LibSection primitives) for consistency
 * with the rest of the redesign.
 *
 * Data source: GET /quick-tap-sessions?featured=1 (legacy envelope —
 * `{objects, count}`). Same backing action as v1
 * (`fetchAllQuickTapFeaturedSessions`). After PR #574 narrowed the
 * paginated-mode trigger set, `?featured=1` alone stays in legacy
 * mode so this page keeps its existing API contract.
 *
 * Drag-end emits the reordered list of IDs via the existing
 * `updateQuickTapFeaturedOrder` action (PUT
 * /quick-tap-sessions/featured-sort) — same payload shape as v1.
 *
 * The page is intentionally not in the design prototype — this is a
 * direct-from-data port that reuses the surface-level conventions of
 * CategoriesTableV2 (rounded sand-bordered table, cream header,
 * hover-revealed edit action, drag handle on the left).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchAllQuickTapFeaturedSessions,
  clearQuickTapSessions,
  updateQuickTapFeaturedOrder,
} from '../../../actions/quickTapsActions';
import {
  LibTable,
  LibSection,
  TierPill,
  IconButton,
  cn,
} from '../../redesign';

/* ─── Icons ─────────────────────────────────────────────────────────── */

const DragHandleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="9"  cy="6"  r="0.5" />
    <circle cx="9"  cy="12" r="0.5" />
    <circle cx="9"  cy="18" r="0.5" />
    <circle cx="15" cy="6"  r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

/* ─── Inline flags (same pattern as CategoriesTableV2) ────────────── */

function InlineFlag({ tone, children }) {
  const BG = {
    dark: '#2C3E50',
    new:  '#75A293',
  };
  return (
    <span
      className="inline-flex items-center font-condensed font-bold uppercase text-white rounded-pill shrink-0"
      style={{
        fontSize: 9.5,
        letterSpacing: '0.08em',
        padding: '1px 6px',
        marginLeft: 4,
        background: BG[tone] || BG.dark,
      }}
    >
      {children}
    </span>
  );
}

/* ─── Sortable row ─────────────────────────────────────────────────── */

function SortableFeaturedRow({ qt, position }) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: qt.id });

  const inactive = !qt.is_active;

  const dragStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 5 : undefined,
  };

  return (
    <LibTable.Row inactive={inactive} ref={setNodeRef} style={dragStyle} {...attributes}>
      {/* Drag handle — `listeners` only here so the rest of the row
       *  stays clickable / hoverable. */}
      <td
        className="border-b border-line text-center group-data-[inactive=true]:bg-[var(--color-row-inactive)] group-data-[inactive=true]:group-hover:bg-[var(--color-row-inactive-hover)]"
        style={{ width: 36, padding: '10px 0 10px 10px' }}
      >
        <span
          {...listeners}
          aria-label="Drag to reorder"
          tabIndex={0}
          className={cn(
            'inline-grid place-items-center text-text-muted cursor-grab',
            'opacity-[0.35] group-hover:opacity-100',
            'hover:bg-cream hover:text-navy group-data-[inactive=true]:hover:bg-[var(--color-row-inactive-hover)] active:cursor-grabbing',
            'focus:outline-none focus-visible:bg-cream group-data-[inactive=true]:focus-visible:bg-[var(--color-row-inactive-hover)] focus-visible:text-navy',
          )}
          style={{
            width: 26,
            height: 26,
            borderRadius: 4,
            transition: 'all 140ms ease',
            touchAction: 'none',
          }}
        >
          <span style={{ width: 13, height: 13, display: 'inline-flex' }}>
            {DragHandleIcon}
          </span>
        </span>
      </td>

      {/* Position counter — JetBrains Mono so the column reads as a
       *  numeric index, not body copy. Helps admins eyeball the order. */}
      <LibTable.NumTd muted>
        {position}
      </LibTable.NumTd>

      {/* Quick Tap — cover thumb + title + inline flags. */}
      <LibTable.Td>
        <div className="flex items-center min-w-0" style={{ gap: 8 }}>
          <div
            className="bg-cream border border-sand shrink-0"
            style={{
              width: 28,
              height: 28,
              borderRadius: 4,
              backgroundImage: qt.image ? `url('${qt.image}')` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <span className="min-w-0 flex items-center" style={{ gap: 4 }}>
            <span className="font-semibold text-navy truncate" style={{ fontSize: 13.5 }}>
              {qt.title || 'Untitled'}
            </span>
            {qt.is_new && <InlineFlag tone="new">New</InlineFlag>}
            {qt.is_dark_mode && <InlineFlag tone="dark">Dark</InlineFlag>}
          </span>
        </div>
      </LibTable.Td>

      {/* Tier — Free / Premium pill (same component the QT list uses). */}
      <LibTable.CenterTd>
        <TierPill isFree={!qt.is_premium} />
      </LibTable.CenterTd>

      {/* Author. */}
      <LibTable.Td>
        <span className="text-text-muted truncate" style={{ fontSize: 13 }}>
          {qt.author?.author_name || '—'}
        </span>
      </LibTable.Td>

      {/* Edit action — hover-revealed, opens v1 edit page (no v2 yet). */}
      <LibTable.Td style={{ textAlign: 'right' }}>
        <div className="inline-flex opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          <Link
            to={`/quick-taps/edit/${qt.id}`}
            aria-label={`Edit ${qt.title}`}
          >
            <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
          </Link>
        </div>
      </LibTable.Td>
    </LibTable.Row>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function QuickTapsFeaturedTableV2() {
  const dispatch = useDispatch();
  // The legacy reducer slice that `fetchAllQuickTapFeaturedSessions`
  // writes to — we share state with the v1 page so a flag flip
  // doesn't trigger a refetch.
  const featured = useSelector((s) => s.quick_taps.all_quick_tap_sessions);

  // Local ordered list — source of truth for the displayed and
  // draggable order. Re-syncs whenever the server list changes (e.g.
  // after a sort save failure).
  const [localList, setLocalList] = useState([]);

  useEffect(() => {
    dispatch(clearQuickTapSessions());
    dispatch(fetchAllQuickTapFeaturedSessions());
  }, [dispatch]);

  useEffect(() => {
    if (!featured) return;
    setLocalList(featured);
  }, [featured]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = localList.findIndex((qt) => qt.id === active.id);
      const newIndex = localList.findIndex((qt) => qt.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return;

      const next = arrayMove(localList, oldIndex, newIndex);
      setLocalList(next);

      // v1 payload shape: JSON-stringified array of quick_tap_ids in
      // the new order. The action handles the PUT and re-fetches on
      // failure, which our `featured` useEffect picks up.
      dispatch(updateQuickTapFeaturedOrder(JSON.stringify(next.map((qt) => qt.id))));
    },
    [localList, dispatch],
  );

  const ids = useMemo(() => localList.map((qt) => qt.id), [localList]);

  if (!featured) {
    return (
      <div className="flex items-center justify-center text-text-muted" style={{ height: 256 }}>
        Loading featured quick taps…
      </div>
    );
  }

  return (
    <div style={{ padding: '0 28px 32px' }}>
      <LibSection
        first
        title="Featured order"
        count={localList.length}
        hint="Drag any row to reorder · sent on each drop"
      >
        {localList.length === 0 ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 12, padding: '20px 16px', fontSize: 13 }}
          >
            No featured quick taps yet. Mark a quick tap as featured to see it here.
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <LibTable
              fixed
              className="border border-sand"
              style={{ borderRadius: 12, overflow: 'hidden' }}
            >
              <colgroup>
                <col style={{ width: 36 }} />
                <col style={{ width: 56 }} />
                <col />
                <col style={{ width: 95 }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: 80 }} />
              </colgroup>
              <LibTable.Head>
                <tr>
                  <LibTable.Th aria-label="Drag to reorder"> </LibTable.Th>
                  <LibTable.NumTh>#</LibTable.NumTh>
                  <LibTable.Th>Quick Tap</LibTable.Th>
                  <LibTable.CenterTh>Tier</LibTable.CenterTh>
                  <LibTable.Th>Author</LibTable.Th>
                  <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
                </tr>
              </LibTable.Head>
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                <tbody>
                  {localList.map((qt, idx) => (
                    <SortableFeaturedRow key={qt.id} qt={qt} position={idx + 1} />
                  ))}
                </tbody>
              </SortableContext>
            </LibTable>
          </DndContext>
        )}
      </LibSection>
    </div>
  );
}
